import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Shield, ArrowRight, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * v1.2 PR-C.1 — Shared org-scope empty-state.
 *
 * Rendered whenever a super_admin lands on an org-scoped page without an
 * active org context. Includes:
 *   - One-sentence explanation.
 *   - INLINE ORG PICKER (super_admin only) — dropdown of every org the user
 *     can see, "View as this organisation" button starts an audit-logged
 *     impersonation session. On success, the AuthContext refresh fires and
 *     the parent page re-renders with `noOrgSuperAdmin=false` automatically.
 *   - Secondary "Open Super admin workspace" button.
 *
 * Previously this lived inline inside SettingsLayout. Hoisting it means
 * every org-scoped page (settings tabs, Users, AuditLog, Billing, Dashboard,
 * etc.) renders the SAME empty-state instead of each one writing its own
 * fallback or — worse — flashing a "No org context" toast.
 */
export default function OrgScopeEmptyState({ heading, subheading }) {
  const navigate = useNavigate();
  const { user, impersonate } = useAuth();
  const [orgs, setOrgs] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [working, setWorking] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    if (!isSuper) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/orgs");
        if (!alive) return;
        setOrgs(data || []);
        if ((data || []).length > 0) setSelectedId(data[0].id);
      } catch (e) {
        if (!alive) return;
        setLoadErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, [isSuper]);

  const goImpersonate = async () => {
    if (!selectedId) return;
    setWorking(true);
    try {
      await impersonate(selectedId);
      const picked = (orgs || []).find((o) => o.id === selectedId);
      toast.success(`Impersonating ${picked?.name || "organisation"} — view logged in audit.`);
      // No navigate(): AuthContext refresh causes the calling page to re-render
      // with noOrgSuperAdmin=false, and its useEffect re-fires the load(). The
      // user stays on the settings sub-page they originally landed on.
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl p-10 md:p-12 text-center max-w-2xl mx-auto"
      data-testid="settings-no-org-empty-state"
    >
      <div className="w-12 h-12 rounded-full bg-navy-50 dark:bg-navy-700 mx-auto mb-4 flex items-center justify-center">
        <Building2 className="w-6 h-6 text-navy-400 dark:text-navy-300" />
      </div>
      <h2 className="text-xl font-semibold text-navy-900 dark:text-white mb-2">
        {heading || "Select an organisation to view this setting"}
      </h2>
      <p className="text-sm text-navy-500 dark:text-navy-300 mb-6 max-w-md mx-auto">
        {subheading || (
          <>Org settings are scoped to a specific organisation. You&apos;re currently signed in as a super admin without an active org context.</>
        )}
      </p>

      {isSuper && (
        <div className="max-w-md mx-auto bg-navy-50 dark:bg-navy-700 rounded-xl p-4 mb-4 text-left" data-testid="empty-state-org-picker">
          <label
            htmlFor="empty-state-org-select"
            className="block text-[11px] uppercase tracking-wider font-semibold text-navy-500 dark:text-navy-200 mb-1.5"
          >
            View as organisation
          </label>
          {orgs === null && !loadErr && (
            <div className="text-xs text-navy-500 dark:text-navy-300 inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading organisations…
            </div>
          )}
          {loadErr && (
            <div className="text-xs text-danger-600" data-testid="empty-state-orgs-error">{loadErr}</div>
          )}
          {orgs !== null && orgs.length === 0 && (
            <div className="text-xs text-navy-500 dark:text-navy-300">No organisations exist yet.</div>
          )}
          {orgs !== null && orgs.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                id="empty-state-org-select"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={working}
                className="eb-input flex-1"
                data-testid="empty-state-org-select"
              >
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <Button
                onClick={goImpersonate}
                disabled={!selectedId || working}
                className="bg-[#FF8A4C] hover:bg-[#FF8A4C]/90 text-white whitespace-nowrap"
                data-testid="empty-state-impersonate-btn"
              >
                {working
                  ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Switching…</>)
                  : (<>View as this organisation <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>)}
              </Button>
            </div>
          )}
          <p className="text-[11px] text-navy-500 dark:text-navy-300 mt-2">
            Starts an audit-logged impersonation session. End it from the orange banner at the top.
          </p>
        </div>
      )}

      <Button
        onClick={() => navigate("/super-admin")}
        variant="outline"
        className="dark:text-navy-100 dark:border-navy-600"
        data-testid="settings-no-org-cta"
      >
        <Shield className="w-4 h-4 mr-1.5" /> Open Super admin workspace
      </Button>
    </div>
  );
}
